import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, Mic2, UserRoundCog, Loader2, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { unwrapApiPayload } from '@/lib/learner-links';
import { toast } from '@/components/ui/Toast';
import { useAuth } from '@/hooks/useAuth';
import type { UserRole } from '@/types';
import type { ApiResponse } from '@/types/api';
import { InstitutionStyledSelect } from '@/components/institution/InstitutionStyledSelect';
import { InstitutionStyledSearchInput } from '@/components/institution/InstitutionStyledSearchInput';
import { isLyrraOne } from '@/lib/platform';

type PodcastProductionStatus = 'shell' | 'assigned' | 'in_production' | 'ready';

interface PersonRef {
  id: string;
  name?: string;
  email?: string;
}

interface InstitutionPodcastRow {
  id: string;
  title: string;
  productionStatus: PodcastProductionStatus;
  episodeCount: number;
  assignedProducer: PersonRef | null;
  createdBy: PersonRef | null;
  createdAt: string;
  updatedAt?: string;
}

const PRODUCTION_STATUS_STYLES: Record<PodcastProductionStatus, string> = {
  shell: 'bg-[#F0F4FA] text-[#0C1C38] dark:bg-[#19335E]/50 dark:text-[#DCE7F7]',
  assigned: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  in_production: 'bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200',
  ready: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
};

/** Aligné sur l’API PATCH …/podcasts/:id/owner (pipeline RH + formateur admin établissement). */
const REASSIGN_PODCAST_OWNER_ROLES: UserRole[] = [
  'institution_admin',
  'institution',
  'drh',
  'rh',
  'resp_formation',
];

type InstitutionUserRow = {
  id: string;
  email: string;
  name: string;
  firstName?: string | null;
  lastName?: string | null;
  roleLabel?: string;
};

function podcastPreviewUrlFromApiPayload(payload: { url?: string; token?: string } | null): string | null {
  if (!payload) return null;
  if (typeof payload.url === 'string' && payload.url.trim()) {
    try {
      const u = new URL(payload.url);
      if (typeof window !== 'undefined') {
        return `${window.location.origin}${u.pathname}${u.search}`;
      }
      return payload.url;
    } catch {
      return payload.url;
    }
  }
  if (typeof payload.token === 'string' && payload.token.trim() && typeof window !== 'undefined') {
    return `${window.location.origin}/podcasts/public?token=${encodeURIComponent(payload.token)}`;
  }
  return null;
}

function unwrapPodcastsPayload(raw: unknown): InstitutionPodcastRow[] {
  const o = raw as Record<string, unknown> | null | undefined;
  if (!o) return [];
  const inner = (o.data as Record<string, unknown> | undefined) ?? o;
  const list = inner.podcasts;
  return Array.isArray(list) ? (list as InstitutionPodcastRow[]) : [];
}

export default function InstitutionSupportsPanel() {
  const { t, i18n } = useTranslation();
  const dateLocale = (i18n.resolvedLanguage ?? i18n.language ?? 'fr').replace('_', '-');
  const { user, hasAnyRole } = useAuth();

  const canReassignPodcastOwner =
    !isLyrraOne() &&
    (hasAnyRole(...REASSIGN_PODCAST_OWNER_ROLES) ||
      hasAnyRole('admin') ||
      (user?.role === 'teacher' && Boolean(user.isInstitutionAdmin)));

  const [podcasts, setPodcasts] = useState<InstitutionPodcastRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const [transferPodcast, setTransferPodcast] = useState<InstitutionPodcastRow | null>(null);
  const [transferCandidates, setTransferCandidates] = useState<InstitutionUserRow[]>([]);
  const [transferUsersLoading, setTransferUsersLoading] = useState(false);
  const [selectedNewOwnerId, setSelectedNewOwnerId] = useState('');
  const [transferSubmitting, setTransferSubmitting] = useState(false);
  const [transferFeedback, setTransferFeedback] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [openingPodcastId, setOpeningPodcastId] = useState<string | null>(null);

  const loadTransferUsers = useCallback(
    async (currentCreatorId?: string) => {
      setTransferUsersLoading(true);
      setTransferFeedback(null);
      try {
        const response = await api.get<ApiResponse<{ users: InstitutionUserRow[] }>>('/institution/users');
        const list = response.data?.users ?? [];
        const filtered = currentCreatorId ? list.filter((u) => u.id !== currentCreatorId) : list;
        setTransferCandidates(filtered);
        setSelectedNewOwnerId('');
      } catch {
        setTransferCandidates([]);
        setTransferFeedback({ type: 'err', text: t('institution.supports.transferError') });
      } finally {
        setTransferUsersLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    if (!transferPodcast) return;
    void loadTransferUsers(transferPodcast.createdBy?.id);
  }, [transferPodcast, loadTransferUsers]);

  const closeTransferModal = () => {
    setTransferPodcast(null);
    setTransferCandidates([]);
    setSelectedNewOwnerId('');
    setTransferFeedback(null);
  };

  const submitTransferOwner = async () => {
    if (!transferPodcast || !selectedNewOwnerId) return;
    setTransferSubmitting(true);
    setTransferFeedback(null);
    try {
      const resp = await api.patch<
        ApiResponse<{ podcast: { id: string; owner: { id: string; email: string; name: string } } }>
      >(`/institution/content/podcasts/${transferPodcast.id}/owner`, { newOwnerId: selectedNewOwnerId });
      const owner = resp.data?.podcast?.owner;
      if (owner) {
        setPodcasts((prev) =>
          prev.map((p) =>
            p.id === transferPodcast.id
              ? {
                  ...p,
                  createdBy: { id: owner.id, name: owner.name, email: owner.email },
                }
              : p,
          ),
        );
      }
      setTransferFeedback({ type: 'ok', text: t('institution.supports.transferSuccess') });
      setTimeout(closeTransferModal, 900);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e
          ? String((e as { message: string }).message)
          : t('institution.supports.transferError');
      setTransferFeedback({ type: 'err', text: msg });
    } finally {
      setTransferSubmitting(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const response = await api.get<unknown>('/institution/content/podcasts');
        if (!mounted) return;
        setPodcasts(unwrapPodcastsPayload(response));
      } catch (e: unknown) {
        if (!mounted) return;
        const msg =
          typeof e === 'object' && e !== null && 'message' in e && typeof (e as { message: unknown }).message === 'string'
            ? (e as { message: string }).message
            : t('institution.supports.loadError');
        setLoadError(msg);
        setPodcasts([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [t]);

  const productionLabel = (s: PodcastProductionStatus) =>
    t(`institution.podcastProduction.${s}`, s.replace('_', ' '));

  const productionStatusFilterOptions = useMemo(
    () => [
      { value: 'all', label: t('institution.supports.allProductionStatuses') },
      { value: 'shell', label: t('institution.podcastProduction.shell', 'shell') },
      { value: 'assigned', label: t('institution.podcastProduction.assigned', 'assigned') },
      { value: 'in_production', label: t('institution.podcastProduction.in_production', 'in production') },
      { value: 'ready', label: t('institution.podcastProduction.ready', 'ready') },
    ],
    [t],
  );

  const filtered = useMemo(() => {
    return podcasts.filter((p) => {
      if (search && !p.title.toLowerCase().includes(search.toLowerCase())) return false;
      if (statusFilter !== 'all' && p.productionStatus !== statusFilter) return false;
      return true;
    });
  }, [search, statusFilter, podcasts]);

  const displayPerson = (p: PersonRef | null) => p?.name || p?.email || t('institution.common.empty');

  const openPodcastPreview = async (podcastId: string) => {
    setOpeningPodcastId(podcastId);
    try {
      const resp = await api.post<unknown>(`/podcasts/${podcastId}/public-share-link`);
      const payload = unwrapApiPayload<{ url?: string; token?: string }>(resp);
      const url = podcastPreviewUrlFromApiPayload(payload);
      if (!url) {
        throw new Error('missing preview url');
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      toast.error(t('institution.supports.previewError'));
    } finally {
      setOpeningPodcastId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#0C1C38] dark:text-white">{t('institution.supports.title')}</h1>
        <p className="mt-1 text-sm text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.supports.subtitle')}</p>
      </div>

      {loadError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
          {loadError}
        </div>
      ) : null}

      {transferPodcast ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="transfer-podcast-owner-title"
        >
          <div className="w-full max-w-md rounded-xl border border-[#E8EDF5] bg-white p-5 shadow-xl dark:border-[#19335E] dark:bg-[#122644]">
            <h2
              id="transfer-podcast-owner-title"
              className="text-lg font-semibold text-[#0C1C38] dark:text-white"
            >
              {t('institution.supports.transferModalTitle')}
            </h2>
            <p className="mt-2 text-sm text-[#5B6472] dark:text-[#B6CAEC]">
              {t('institution.supports.transferModalHint')}
            </p>
            <p
              className="mt-2 truncate text-sm font-medium text-[#0C1C38] dark:text-[#DCE7F7]"
              title={transferPodcast.title}
            >
              {transferPodcast.title}
            </p>
            {transferUsersLoading ? (
              <p className="mt-3 text-sm text-[#5B6472]">{t('institution.supports.transferLoadingUsers')}</p>
            ) : transferCandidates.length === 0 ? (
              <p className="mt-3 text-sm text-amber-700 dark:text-amber-400">
                {t('institution.supports.transferNoCandidates')}
              </p>
            ) : (
              <div className="mt-3">
                <InstitutionStyledSelect
                  label={t('institution.supports.transferPickUser')}
                  panelZIndexClass="z-[200]"
                  options={[
                    { value: '', label: '—' },
                    ...transferCandidates.map((u) => ({
                      value: u.id,
                      label: `${u.name || `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email}${u.roleLabel ? ` (${u.roleLabel})` : ''}`,
                      icon: User,
                    })),
                  ]}
                  value={selectedNewOwnerId}
                  onChange={setSelectedNewOwnerId}
                />
              </div>
            )}
            {transferFeedback ? (
              <p
                className={cn(
                  'mt-3 text-sm',
                  transferFeedback.type === 'ok'
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-red-600 dark:text-red-400',
                )}
              >
                {transferFeedback.text}
              </p>
            ) : null}
            <div className="mt-4 flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={closeTransferModal}
                className="rounded-lg border border-[#E8EDF5] px-3 py-1.5 text-sm text-[#121826] hover:bg-[#F8FAFC] dark:border-[#B6CAEC] dark:text-[#DCE7F7] dark:hover:bg-[#19335E]"
              >
                {t('institution.common.cancel')}
              </button>
              <button
                type="button"
                disabled={!selectedNewOwnerId || transferSubmitting || transferUsersLoading}
                onClick={() => void submitTransferOwner()}
                className="rounded-lg bg-primary-900 px-3 py-1.5 text-sm text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {transferSubmitting ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('institution.common.loading')}
                  </span>
                ) : (
                  t('institution.supports.transferSubmit')
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-3">
        <div className="flex-1 min-w-[200px]">
          <InstitutionStyledSearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('institution.supports.searchPh')}
          />
        </div>
        <div className="min-w-[200px] shrink-0">
          <InstitutionStyledSelect
            options={productionStatusFilterOptions}
            value={statusFilter}
            onChange={setStatusFilter}
          />
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-[#E8EDF5] bg-white dark:border-[#19335E] dark:bg-[#122644]">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E8EDF5] bg-[#F8FAFC] dark:border-[#19335E] dark:bg-[#122644]/50">
                <th className="px-4 py-3 text-start font-medium text-[#5B6472] dark:text-[#B6CAEC]">
                  {t('institution.common.title')}
                </th>
                <th className="px-4 py-3 text-start font-medium text-[#5B6472] dark:text-[#B6CAEC]">
                  {t('institution.supports.colKind')}
                </th>
                <th className="px-4 py-3 text-start font-medium text-[#5B6472] dark:text-[#B6CAEC]">
                  {t('institution.supports.colEpisodes')}
                </th>
                <th className="px-4 py-3 text-start font-medium text-[#5B6472] dark:text-[#B6CAEC]">
                  {t('institution.common.status')}
                </th>
                <th className="px-4 py-3 text-start font-medium text-[#5B6472] dark:text-[#B6CAEC]">
                  {t('institution.supports.colProducer')}
                </th>
                <th className="px-4 py-3 text-start font-medium text-[#5B6472] dark:text-[#B6CAEC]">
                  {t('institution.supports.colCreator')}
                </th>
                <th className="px-4 py-3 text-start font-medium text-[#5B6472] dark:text-[#B6CAEC]">
                  {t('institution.common.createdAt')}
                </th>
                <th className="px-4 py-3 text-end font-medium text-[#5B6472] dark:text-[#B6CAEC]">
                  {t('institution.common.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F0F4FA] dark:divide-[#19335E]">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-[#5B6472]/70 dark:text-[#B6CAEC]">
                    {t('institution.common.loading')}
                  </td>
                </tr>
              ) : (
                filtered.map((p) => (
                  <tr key={p.id} className="transition-colors hover:bg-[#F8FAFC] dark:hover:bg-[#19335E]/30">
                    <td className="px-4 py-3 font-medium text-[#0C1C38] dark:text-white">
                      <span className="inline-flex items-center gap-2">
                        <Mic2 className="h-4 w-4 flex-shrink-0 text-[#5B6472]/70" aria-hidden />
                        {p.title}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[#5B6472] dark:text-[#DCE7F7]">{t('institution.supports.typePodcast')}</td>
                    <td className="px-4 py-3 text-[#5B6472] dark:text-[#DCE7F7]">{p.episodeCount}</td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'rounded-full px-2 py-0.5 text-xs font-medium',
                          PRODUCTION_STATUS_STYLES[p.productionStatus] ?? PRODUCTION_STATUS_STYLES.shell,
                        )}
                      >
                        {productionLabel(p.productionStatus)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[#5B6472] dark:text-[#DCE7F7]">{displayPerson(p.assignedProducer)}</td>
                    <td className="px-4 py-3 text-[#5B6472] dark:text-[#DCE7F7]">{displayPerson(p.createdBy)}</td>
                    <td className="px-4 py-3 text-[#5B6472] dark:text-[#B6CAEC]">
                      {new Date(p.createdAt).toLocaleDateString(dateLocale)}
                    </td>
                    <td className="px-4 py-3 text-end">
                      <div className="inline-flex items-center justify-end gap-1">
                        <button
                          type="button"
                          title={t('institution.common.view')}
                          disabled={openingPodcastId === p.id}
                          onClick={() => void openPodcastPreview(p.id)}
                          className="rounded-lg p-1.5 text-[#5B6472] hover:bg-[#F0F4FA] hover:text-[#0C1C38] disabled:opacity-50 dark:text-[#B6CAEC] dark:hover:bg-[#19335E] dark:hover:text-white"
                        >
                          {openingPodcastId === p.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                          ) : (
                            <Eye className="h-4 w-4" aria-hidden />
                          )}
                        </button>
                        {canReassignPodcastOwner ? (
                          <button
                            type="button"
                            title={t('institution.supports.transferOwner')}
                            onClick={() => setTransferPodcast(p)}
                            className="inline-flex rounded-lg p-1.5 text-primary-600 hover:bg-[#F0F4FA] dark:text-primary-400 dark:hover:bg-[#19335E]"
                          >
                            <UserRoundCog className="h-4 w-4" aria-hidden />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              )}
              {!loading && filtered.length === 0 && !loadError ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-[#5B6472]/70 dark:text-[#B6CAEC]">
                    {t('institution.supports.empty')}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
